import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { PluginCard, PluginCardSkeleton } from "../components/plugins/PluginCard";
import {
  ArrowLeftIcon,
  CopyIcon,
  CheckIcon,
  CodeIcon,
  ShareIcon,
  TrendingUpIcon,
  ZapIcon,
  UploadIcon,
} from "../components/ui/Icons";
import { pluginApi, type Plugin } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

// Pack metadata — defines the packs and how to display them
const PACK_META: Record<string, { name: string; description: string; color: string }> = {
  developer: {
    name: "Developer",
    description:
      "Essential tools for software development — monorepo setup, design systems, and code architecture.",
    color: "blue",
  },
  "social-media": {
    name: "Social Media",
    description:
      "Automate your social presence — navigate Facebook, X.com, and manage content across platforms.",
    color: "purple",
  },
  marketing: {
    name: "Marketing",
    description: "Content creation, SEO analysis, campaign planning, and marketing workflows.",
    color: "amber",
  },
  productivity: {
    name: "Productivity",
    description:
      "Streamline your workflow — email automation, browser tasks, and daily operations.",
    color: "green",
  },
  deployment: {
    name: "Deployment",
    description: "Deploy with confidence — Cloudflare, Heroku, and hosting platform integrations.",
    color: "red",
  },
};

export function getPackIcon(packId: string, size = 20) {
  switch (packId) {
    case "developer":
      return <CodeIcon size={size} />;
    case "social-media":
      return <ShareIcon size={size} />;
    case "marketing":
      return <TrendingUpIcon size={size} />;
    case "productivity":
      return <ZapIcon size={size} />;
    case "deployment":
      return <UploadIcon size={size} />;
    default:
      return <CodeIcon size={size} />;
  }
}

export function getPackMeta(packId: string) {
  return PACK_META[packId] || { name: packId, description: "", color: "amber" };
}

export function getAllPackIds() {
  return Object.keys(PACK_META);
}

// Helper to map Plugin to PluginCard props (same as LandingPage)
function mapPluginToCardProps(plugin: Plugin) {
  return {
    id: plugin.id,
    slug: plugin.slug,
    name: plugin.name,
    description: plugin.shortDesc || plugin.description,
    author: plugin.authorName || plugin.author.name,
    tags: plugin.tags,
    downloads: plugin._count?.downloads || 0,
    stars: plugin._count?.favorites || 0,
    version: plugin.versions[0]?.version,
    featured: plugin.featured,
    files: plugin.files.map((f) => ({
      id: f.id,
      fileType: f.fileType,
      displayName: f.displayName,
      fileName: f.fileName,
      description: f.description,
    })),
    pricingModel: plugin.pricingModel,
    price: plugin.price,
  };
}

export function PackLandingPage() {
  const { packId } = useParams<{ packId: string }>();
  const { isAuthenticated } = useAuth();
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const pack = packId ? getPackMeta(packId) : null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const data = await pluginApi.getPublishedPlugins();
        // Filter plugins that have the pack tag
        const packPlugins = data.filter((p) => p.tags?.some((tag) => tag === `pack:${packId}`));
        setPlugins(packPlugins);
        setError(null);

        if (isAuthenticated) {
          try {
            const userFavorites = await pluginApi.getUserFavorites();
            setFavorites(new Set(userFavorites));
          } catch {
            // Ignore favorites fetch errors
          }
        }
      } catch (err) {
        console.error("Failed to fetch plugins:", err);
        setError("Failed to load plugins");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [packId, isAuthenticated]);

  const handleStar = async (pluginId: string) => {
    if (!isAuthenticated) return;

    try {
      const result = await pluginApi.toggleFavorite(pluginId);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (result.favorited) {
          next.add(pluginId);
        } else {
          next.delete(pluginId);
        }
        return next;
      });

      setPlugins((prev) =>
        prev.map((p) =>
          p.id === pluginId ? { ...p, _count: { ...p._count, favorites: result.count } } : p
        )
      );
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
    }
  };

  const copyInstallCommand = (slug: string) => {
    navigator.clipboard.writeText(`claude plugin install ${slug}@superchargeclaudecode`);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  if (!pack || !packId) {
    return (
      <div className="min-h-screen bg-base">
        <Header />
        <div className="pt-16">
          <div className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-6 lg:px-8">
            <h1 className="text-2xl font-bold text-primary">Pack not found</h1>
            <Link to="/" className="mt-4 inline-block text-amber hover:underline">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base">
      <Header />

      <div className="pt-16">
        {/* Pack Header */}
        <section className="border-b border-surface">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <Link
              to="/#packs"
              className="mb-6 inline-flex items-center gap-2 font-mono text-sm text-muted transition-colors hover:text-secondary"
            >
              <ArrowLeftIcon size={14} />
              All Packs
            </Link>

            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-amber/20 text-amber">
                {getPackIcon(packId, 28)}
              </div>
              <div>
                <h1 className="font-display text-3xl font-bold text-primary md:text-4xl">
                  {pack.name} Pack
                </h1>
                <p className="mt-2 max-w-2xl text-lg text-muted">{pack.description}</p>
                <p className="mt-3 font-mono text-sm text-muted">
                  {isLoading ? "..." : `${plugins.length} plugin${plugins.length !== 1 ? "s" : ""}`}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Plugin List */}
        <section className="py-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {isLoading ? (
              <div className="columns-1 gap-6 md:columns-2 lg:columns-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="mb-6 break-inside-avoid">
                    <PluginCardSkeleton />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="py-12 text-center">
                <p className="text-muted">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 rounded-lg bg-amber px-4 py-2 text-sm font-medium text-base hover:bg-amber-hover"
                >
                  Try Again
                </button>
              </div>
            ) : plugins.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-lg text-secondary">No plugins in this pack yet</p>
                <p className="mt-2 text-muted">Check back soon for new additions.</p>
              </div>
            ) : (
              <>
                {/* Plugin Cards */}
                <div className="columns-1 gap-6 md:columns-2 lg:columns-3">
                  {plugins.map((plugin) => (
                    <div key={plugin.id} className="mb-6 break-inside-avoid">
                      <PluginCard
                        {...mapPluginToCardProps(plugin)}
                        starred={favorites.has(plugin.id)}
                        onStar={handleStar}
                      />
                    </div>
                  ))}
                </div>

                {/* Install All Section */}
                <div className="mt-12 rounded-xl border border-elevated bg-surface/50 p-6">
                  <h3 className="mb-4 font-display text-lg font-bold text-primary">
                    Install individually
                  </h3>
                  <div className="space-y-2">
                    {plugins.map((plugin) => (
                      <div
                        key={plugin.id}
                        className="flex items-center justify-between gap-3 rounded-lg bg-base/50 px-4 py-2"
                      >
                        <code className="truncate font-mono text-sm text-secondary">
                          claude plugin install {plugin.slug}@superchargeclaudecode
                        </code>
                        <button
                          onClick={() => copyInstallCommand(plugin.slug)}
                          className="flex shrink-0 items-center gap-1 rounded-md bg-elevated px-2 py-1 text-xs text-muted transition-colors hover:text-secondary"
                        >
                          {copiedSlug === plugin.slug ? (
                            <>
                              <CheckIcon size={12} />
                              Copied
                            </>
                          ) : (
                            <>
                              <CopyIcon size={12} />
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
